import { useEffect, useCallback } from 'react';
import { useTokenStore } from '../stores/tokenStore';
import { useEffectStore } from '../stores/effectStore';
import { useUndoStore } from '../stores/undoStore';
import type { Token } from '@machina/core';

/**
 * Map Keyboard Hook
 *
 * Handles keyboard shortcuts for the map canvas:
 * - Delete/Backspace: Remove selected tokens or selected spell effect
 * - Ctrl+Z / Cmd+Z: Undo
 * - Ctrl+Y / Cmd+Shift+Z: Redo
 */
export function useMapKeyboard(isGM: boolean) {
  const selectedIds = useTokenStore((s) => s.selectedIds);
  const tokens = useTokenStore((s) => s.tokens);
  const removeToken = useTokenStore((s) => s.removeToken);
  const restoreToken = useTokenStore((s) => s.restoreToken);
  const clearSelection = useTokenStore((s) => s.clearSelection);

  const push = useUndoStore((s) => s.push);
  const undo = useUndoStore((s) => s.undo);
  const redo = useUndoStore((s) => s.redo);

  // Delete selected tokens with undo support
  // Note: GM-only — player character tokens are also protected from deletion
  const deleteSelected = useCallback(() => {
    if (!isGM) return;
    if (selectedIds.size === 0) return;

    // Capture tokens to delete for undo (skip player-controlled tokens)
    const tokensToDelete: Token[] = [];
    for (const id of selectedIds) {
      const token = tokens.get(id);
      if (token && !token.isPlayerControlled) {
        tokensToDelete.push({ ...token });
      }
    }

    if (tokensToDelete.length === 0) return;

    // Remove the tokens
    for (const token of tokensToDelete) {
      removeToken(token.id);
    }
    clearSelection();

    // Push undo action
    push({
      type: 'token:remove',
      description: `Delete ${tokensToDelete.length} token(s)`,
      undo: () => {
        // Restore all deleted tokens with their original properties
        for (const token of tokensToDelete) {
          restoreToken(token);
        }
      },
      redo: () => {
        // Re-delete the tokens
        for (const token of tokensToDelete) {
          removeToken(token.id);
        }
      },
    });
  }, [isGM, selectedIds, tokens, removeToken, restoreToken, clearSelection, push]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Delete/Backspace - delete selected spell effect first, then tokens
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const effectState = useEffectStore.getState();
        if (effectState.selectedEffectId) {
          effectState.deleteSelected();
          return;
        }
        deleteSelected();
        return;
      }

      // Ctrl+Z / Cmd+Z - Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }

      // Ctrl+Y / Cmd+Shift+Z - Redo
      if (
        (e.ctrlKey || e.metaKey) && e.key === 'y' ||
        (e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z'
      ) {
        e.preventDefault();
        redo();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteSelected, undo, redo]);

  return { deleteSelected };
}
